import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { Notification, NotificationType } from '@workspace/lib/api/notification'

// Extend dayjs with plugins
dayjs.extend(utc)
dayjs.extend(customParseFormat)

/**
 * Represents a parsed part of notification content
 */
export interface NotificationContentPart {
    text: string
    bold?: boolean
    className?: string
    key: string
}

interface ContentParseOptions {
    /**
     * For approval notifications, highlight specific keywords with colors
     * - 'approved': "duyệt" → green
     * - 'rejected': "từ chối" → red
     */
    highlightApprovalStatus?: boolean
}

/**
 * Parses notification content into structured parts for rendering
 * Pure function - no JSX, returns data structure only
 *
 * @param notification - The notification object from API
 * @param options - Optional parsing options
 * @returns Array of content parts with formatting metadata
 */
export function parseNotificationContent(
    notification: Notification,
    options: ContentParseOptions = {},
): NotificationContentPart[] {
    const { highlightApprovalStatus = false } = options

    let content = notification.content || ''
    const { type, metadata, task } = notification

    // Format date if present
    const formattedDueDate = metadata?.dueDate ? dayjs(metadata.dueDate, 'DD/MM/YYYY HH:mm').format('DD/MM/YYYY') : ''

    if (formattedDueDate && metadata?.dueDate && content.includes(metadata.dueDate)) {
        content = content.replace(metadata.dueDate, formattedDueDate)
    }

    // Build list of items to bold
    const boldItems: { text: string; key: string; className?: string }[] = []

    switch (type) {
        case 'task_assigned':
            if (metadata?.createdBy?.fullName && content.includes(metadata.createdBy.fullName)) {
                boldItems.push({ text: metadata.createdBy.fullName, key: 'actor' })
            }
            if (task?.title && content.includes(task.title)) {
                boldItems.push({ text: task.title, key: 'task' })
            }
            break

        case 'task_overdue':
        case 'task_neardue':
        case 'task_deadline_changed':
            if (task?.title && content.includes(task.title)) {
                boldItems.push({ text: task.title, key: 'task' })
            }
            break

        case 'task_comment':
        case 'new_request_approval':
            // Unified: Both types show actor name + task title
            // Actor can be from: requester (User object) or metadata (string or object)
            const actorName = metadata?.createdBy?.fullName || metadata?.requester
            if (actorName && content.includes(actorName)) {
                boldItems.push({ text: actorName, key: 'actor' })
            }
            if (task?.title && content.includes(task.title)) {
                boldItems.push({ text: task.title, key: 'task' })
            }
            break

        case 'request_approval_rejected':
            if (metadata?.rejecter && content.includes(metadata.rejecter)) {
                boldItems.push({ text: metadata.rejecter, key: 'rejecter' })
            }
            // Highlight "từ chối" in red
            if (highlightApprovalStatus && content.includes('từ chối')) {
                boldItems.push({
                    text: 'từ chối',
                    key: 'rejected',
                    className: 'text-red-500',
                })
            }
            break

        case 'request_approval_approved':
            if (metadata?.approver && content.includes(metadata.approver)) {
                boldItems.push({ text: metadata.approver, key: 'approver' })
            }
            // Highlight "duyệt" in green
            if (highlightApprovalStatus && content.includes('duyệt')) {
                boldItems.push({
                    text: 'duyệt',
                    key: 'approved',
                    className: 'text-green-500',
                })
            }
            break

        case 'request_approval_reminder':
            const reminderActorName = metadata?.requesterName || metadata?.requester
            if (reminderActorName && content.includes(reminderActorName)) {
                boldItems.push({ text: reminderActorName, key: 'actor' })
            }
            if (task?.title && content.includes(task.title)) {
                boldItems.push({ text: task.title, key: 'task' })
            }
            if (metadata?.dayCount) {
                const dayStr = `[Nhắc nhở ngày thứ ${metadata.dayCount}]`
                if (content.includes(dayStr)) {
                    boldItems.push({ text: dayStr, key: 'reminder_day', className: 'text-orange-500 font-bold' })
                }
            }
            break

        case 'project_item_changed': {
            const actorName = metadata?.requesterName || metadata?.createdBy?.fullName
            if (actorName && content.includes(actorName)) {
                boldItems.push({ text: actorName, key: 'actor' })
            }
            if (metadata?.itemName) {
                const quotedItemName = `"${metadata.itemName}"`
                if (content.includes(quotedItemName)) {
                    boldItems.push({ text: quotedItemName, key: 'itemName' })
                } else if (content.includes(metadata.itemName)) {
                    boldItems.push({ text: metadata.itemName, key: 'itemName' })
                }
            }
            if (metadata?.projectName && content.includes(metadata.projectName)) {
                boldItems.push({ text: metadata.projectName, key: 'projectName' })
            }
            break
        }

        case 'weekly_report_created':
        case 'weekly_report_updated': {
            if (metadata?.senderName && content.includes(metadata.senderName)) {
                boldItems.push({ text: metadata.senderName, key: 'senderName' })
            }
            if (metadata?.week && metadata?.year) {
                const weekStr = `tuần ${metadata.week}/${metadata.year}`
                if (content.includes(weekStr)) {
                    boldItems.push({ text: weekStr, key: 'week' })
                } else if (content.includes(`${metadata.week}/${metadata.year}`)) {
                    boldItems.push({ text: `${metadata.week}/${metadata.year}`, key: 'week' })
                }
            }
            if (metadata?.startDate && metadata?.endDate) {
                const dateStr = `(${metadata.startDate} - ${metadata.endDate})`
                if (content.includes(dateStr)) {
                    boldItems.push({ text: dateStr, key: 'date' })
                }
            }
            if (metadata?.areaName && content.includes(metadata.areaName)) {
                boldItems.push({ text: metadata.areaName, key: 'area' })
            }
            break
        }

        case 'subordinate_picked_task_from_other_unit': {
            if (metadata?.assigneeName && content.includes(metadata.assigneeName)) {
                boldItems.push({ text: metadata.assigneeName, key: 'assignee' })
            }
            if (metadata?.taskTitle) {
                // Handle both old DB records with quotes `"taskTitle"` and new records without
                const quotedTitle = `"${metadata.taskTitle}"`
                if (content.includes(quotedTitle)) {
                    boldItems.push({ text: quotedTitle, key: 'task' })
                } else if (content.includes(metadata.taskTitle)) {
                    boldItems.push({ text: metadata.taskTitle, key: 'task' })
                }
            }
            if (metadata?.unitAName && content.includes(metadata.unitAName)) {
                boldItems.push({ text: metadata.unitAName, key: 'unit' })
            }
            break
        }
    }

    // If no items to bold, return plain content as single part
    if (boldItems.length === 0) {
        return [{ text: content, key: 'content' }]
    }

    // Build regex to split content by bold items. Removing restrictive lookahead to match exact strings properly even before special characters.
    const escapedItems = boldItems.map(item => item.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    const regex = new RegExp(`(${escapedItems.join('|')})`, 'g')
    const parts = content.split(regex).filter(Boolean)

    // Convert to structured parts
    return parts.map((part, index) => {
        const boldItem = boldItems.find(item => item.text === part)
        if (boldItem) {
            return {
                text: part,
                bold: true,
                className: boldItem.className || 'font-semibold text-foreground',
                key: `${boldItem.key}-${index}`,
            }
        }
        return {
            text: part,
            key: `text-${index}`,
        }
    })
}

/**
 * Configuration for toast icon based on notification type
 */
export interface ToastIconConfig {
    type: 'icon' | 'avatar'
    icon?: 'task' | 'check' | 'x'
    iconClass?: string
    bgClass?: string
}

/**
 * Gets the icon configuration for toast based on notification type
 */
export function getToastIconConfig(type: NotificationType): ToastIconConfig {
    switch (type) {
        case 'task_assigned':
        case 'subordinate_picked_task_from_other_unit':
        case 'task_overdue':
        case 'task_neardue':
        case 'task_deadline_changed':
            return {
                type: 'icon',
                icon: 'task',
                iconClass: 'text-primary',
                bgClass: 'bg-primary-50',
            }

        case 'weekly_report_created':
        case 'weekly_report_updated':
            return {
                type: 'icon',
                icon: 'task',
                iconClass: 'text-primary',
                bgClass: 'bg-primary-50',
            }

        case 'request_approval_approved':
            return {
                type: 'icon',
                icon: 'check',
                iconClass: 'text-green-500',
                bgClass: 'bg-green-50',
            }

        case 'request_approval_rejected':
            return {
                type: 'icon',
                icon: 'x',
                iconClass: 'text-red-500',
                bgClass: 'bg-red-50',
            }

        case 'request_approval_reminder':
            return {
                type: 'icon',
                icon: 'task',
                iconClass: 'text-orange-500',
                bgClass: 'bg-orange-50',
            }

        case 'task_comment':
        case 'new_request_approval':
        case 'project_item_changed':
        default:
            return {
                type: 'avatar',
            }
    }
}
